import { tokens } from './tokens';

export function resolveCanvasLightColor(): string {
  return tokens.color.canvas.light ?? 'var(--canvas-light, #F5F7F6)';
}

export function resolveVectorFallbackColor(): string {
  return tokens.color.accent.primary;
}

export function resolveOverlayWarningColor(): string {
  return tokens.color.semantic.warning;
}

// ---------------------------------------------------------------------------
// WP-3 — on-canvas state colors and text contrast helpers.
//
// MapLibre paint props can only accept concrete color strings (it does not
// resolve CSS custom properties at runtime). These resolvers route every
// on-canvas color through the design bridge so component code never carries
// raw #hex literals. The function names mirror the binding tokens called out
// in masterplan §8.3:
//
//   - `--state-warning-on-canvas`  → resolveStateWarningOnCanvas()
//   - `--state-error-on-canvas`    → resolveStateErrorOnCanvas()
//   - `--text-on-canvas-light`     → resolveTextOnCanvasLight()
//   - `--text-halo-on-canvas-light`→ resolveTextHaloOnCanvasLight()
//
// The technical canvas (P&ID / map / profile) stays light per
// `geosoft-platform-design-system/docs/UIUX_DETAILED_SURFACE_STUDY.md` §3,
// so text painted on it must be dark for AA contrast.
// ---------------------------------------------------------------------------

export function resolveStateWarningOnCanvas(): string {
  return tokens.color.semantic.warning;
}

export function resolveStateErrorOnCanvas(): string {
  return tokens.color.semantic.danger;
}

export function resolveTextOnCanvasLight(): string {
  return tokens.color.ink.inverse;
}

export function resolveTextHaloOnCanvasLight(): string {
  return tokens.color.canvas.light ?? tokens.color.ink.primary;
}

export function tokenColorToRgba(
  color: string,
  alpha = 255,
): [number, number, number, number] {
  const normalized = color.trim();
  if (normalized.startsWith('#') && (normalized.length === 7 || normalized.length === 9)) {
    const r = Number.parseInt(normalized.slice(1, 3), 16);
    const g = Number.parseInt(normalized.slice(3, 5), 16);
    const b = Number.parseInt(normalized.slice(5, 7), 16);
    const a = normalized.length === 9 ? Number.parseInt(normalized.slice(7, 9), 16) : alpha;
    return [r, g, b, a];
  }
  return tokenColorToRgba(tokens.color.accent.primary, alpha);
}

/**
 * Convert an integer color channel triple (0–255 each) into a 7-character
 * `#rrggbb` string. Used by the raster pixel-inspector pick row to display
 * a tokenized hex code for a sampled pixel instead of a live `rgb(...)`
 * literal in component code.
 */
export function channelTripleToHex(values: readonly number[]): string {
  const toByte = (n: number) => {
    const clamped = Math.max(0, Math.min(255, Math.round(n)));
    return clamped.toString(16).padStart(2, '0');
  };
  const r = toByte(values[0] ?? 0);
  const g = toByte(values[1] ?? 0);
  const b = toByte(values[2] ?? 0);
  return `#${r}${g}${b}`;
}
